import { SelectMultipleControlValueAccessor } from '@angular/forms';
import { googleAnalyticsSubmitForm } from '../../../js/datalayer'
import * as Pristine from '../../../js/vendor/Pristine'

const PHONE_ALL_REG = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
//Pass (example): 1234567890 / 123-456-7890 / (001) 123 456789 / +(001).123.456789 / +(001)-123-456789
const ZIP_US_CAN_REG = /(^\d{5}([ \-]\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1}[ \-]\d{1}[A-Z]{1}\d{1}$)/;
//Pass (example): (CAN) R2H 1L5 / R2H-1L5 --- (USA) 90210 / 90210-1234 / 90210 1234
const FILE_EXT_REG = /([a-zA-Z0-9\s_\\.\-\(\):])+(.doc|.docx|.xls|.xlsx|.txt|.pdf|.jpeg|.jpg|.png|.svg)$/i;

const FORM_SELECTORS = {
  VALIDATION_CONTAINER: 'form__group',
  VALIDATION_ERROR: 'error',
  HINT_CONTAINER: ':scope .form__hint',
  SUMMARY_CONTAINER: ':scope .form__validation-summary',
  ACTIVE_ERROR_LISTS: ':scope .form__group.error',
  ACTIVE_ERROR_LIST_ITEM: '.form__error-message',
  INPUT_NON_HIDDEN: "input:not([type='hidden'])",
  ERROR_MESSAGE_REQUIRED: '.form__error-message.required',
  ERROR_MESSAGE_VALIDATION: '.form__error-message.validation',
}

const VALIDATION_SUMMARY_TITLE = 'Validation Errors:'
const VALIDATION_SUMMARY_ATTR = 'data-summary-title'

const FORM_CLASSES = {
  SUMMARY_CONTAINER_VISIBLE: 'form__validation-summary--visible',
  ERROR_HIDE_ERROR: 'hide-error',
}

const FormValidation = {
  init: function (element, validationEnabled = true) {    
    element.formValidator = this;
    this.form = element
    this.validationEnabled = validationEnabled    
    this.form.addEventListener('submit', this.onSubmitForm.bind(this))
    this.form.addEventListener('reset', this.resetValidationFromUI.bind(this))

    if (!validationEnabled) {
      return
    }

    this.pristineFormInstance = new Pristine(element, {
      classTo: FORM_SELECTORS.VALIDATION_CONTAINER,
      errorClass: FORM_SELECTORS.VALIDATION_ERROR,
      onLiveCompleted: this.onLiveValidationCompleted.bind(this),
    });

    //add phone validation if element exists
    const inputPhone = this.form.querySelector('#Phone');
    if (inputPhone) {
        this.pristineFormInstance.addValidator(inputPhone, function(value) {
            return PHONE_ALL_REG.test(value);
        });
    }

    //add zip validation if element exists
    const inputZip = this.form.querySelector('#ZipCode');
    if (inputZip) {
        this.pristineFormInstance.addValidator(inputZip, function(value) {
            return ZIP_US_CAN_REG.test(value);
        });
    }

    //add file validation if element exists
    const uploadField = this.form.querySelector('#Upload');
    if (uploadField)
    {
      this.pristineFormInstance.addValidator(uploadField, function(value) {
        return FILE_EXT_REG.test(value)
      });
    }

    // enable upload enabled
    const uploadType = this.form.querySelector('#UploadType');
    if (uploadType)
    {
      uploadType.addEventListener('change', function(e) {
        if (e.target.value.length > 0)
        {
          uploadField.removeAttribute('disabled');
        }
      });
    }
  },

  createValidationSummaryItem: function (text) {
    const item = document.createElement('div')
    item.textContent = text
    return item
  },

  onLiveValidationCompleted: function () {
    this.showSummary(false)
  },

  hideSummary: function () {
    const summaryContainer = this.form.querySelector(
      FORM_SELECTORS.SUMMARY_CONTAINER
    )
    summaryContainer.classList.remove(FORM_CLASSES.SUMMARY_CONTAINER_VISIBLE)
    summaryContainer.setAttribute(VALIDATION_SUMMARY_ATTR, '')
  },

  showSummary: function (scrollToSummary = true) {
    const domErrors = this.form.querySelectorAll(
      FORM_SELECTORS.ACTIVE_ERROR_LISTS
    )
    const domErrorsList = Array.from(domErrors)
    if (domErrorsList.length) {
      const activeErrorList = domErrorsList
        .map((item) => {
          const errorFeedbackItems = item.querySelectorAll(
            FORM_SELECTORS.ACTIVE_ERROR_LIST_ITEM
          )
          if (errorFeedbackItems.length > 1) {
            return this.processFormGroupErrors(item)
          } else {
            return errorFeedbackItems[0].textContent
          }
        })
        .filter(Boolean)

      this.updateValidationSummary(activeErrorList, scrollToSummary)
    } else {
      this.hideSummary()
    }
  },

  /**
   *
   * @param {HtmlEntity} formGroup Form Group that includes: input(text/select/etc), error-message(s), input(s)hidden
   * This method show or hide 'error-message(s)' to have only one error-message visible at the same time.
   * Also return the appropriate text to show in the summary errors section.
   * @returns {String} A text to add in errors summary block
   */
  processFormGroupErrors: function (formGroup) {
    const formGroupInput = formGroup.querySelector(
      FORM_SELECTORS.INPUT_NON_HIDDEN
    )
    // if the input has some value, show only 'validation' error-message (and hide 'required' error-message).
    if (String(formGroupInput.value).trim() !== '') {
      //get 'required' error-message and hide
      const requiredError = formGroup.querySelector(
        FORM_SELECTORS.ERROR_MESSAGE_REQUIRED
      )
      if (requiredError) {
        requiredError.classList.add(FORM_CLASSES.ERROR_HIDE_ERROR)
      }
      //get 'validation' error-message and show
      const nonRequiredError = formGroup.querySelector(
        FORM_SELECTORS.ERROR_MESSAGE_VALIDATION
      )
      if (nonRequiredError) {
        nonRequiredError.classList.remove(FORM_CLASSES.ERROR_HIDE_ERROR)
        return nonRequiredError.textContent
      }

      // if the input is empty, show only 'required' error-message (and hide 'validation' error-message).
    } else {
      //get 'validation' error-message and hide
      const nonRequiredError = formGroup.querySelector(
        FORM_SELECTORS.ERROR_MESSAGE_VALIDATION
      )
      if (nonRequiredError) {
        nonRequiredError.classList.add(FORM_CLASSES.ERROR_HIDE_ERROR)
      }
      //get 'required' error-message and show
      const requiredError = formGroup.querySelector(
        FORM_SELECTORS.ERROR_MESSAGE_REQUIRED
      )
      if (requiredError) {
        requiredError.classList.remove(FORM_CLASSES.ERROR_HIDE_ERROR)
        return requiredError.textContent
      }
    }
    return ''
  },

  updateValidationSummary: function (activeErrorList, scrollTo) {
    const summaryContainer = this.form.querySelector(
      FORM_SELECTORS.SUMMARY_CONTAINER
    )
    const domActiveErrorList = activeErrorList.map((error) =>
      this.createValidationSummaryItem(error)
    )
    summaryContainer && summaryContainer.replaceChildren(...domActiveErrorList)
    summaryContainer.classList.add(FORM_CLASSES.SUMMARY_CONTAINER_VISIBLE)
    summaryContainer.setAttribute(
      VALIDATION_SUMMARY_ATTR,
      VALIDATION_SUMMARY_TITLE
    )
    if (scrollTo) {
      this.scrollToContainer(summaryContainer);
    }
  },

  scrollToContainer: function (scrollToObj, opts = { behavior: 'smooth', block: 'center' }) {
    [scrollToObj && typeof scrollToObj === 'string' ?
      document.querySelector(scrollToObj) :
      scrollToObj]
        .filter((obj) => obj && obj.scrollIntoView)
        .forEach((item) => {
          item.scrollIntoView(opts);
        })
  },

  onSubmitForm: function (evt) {
    if (!this.validationEnabled) {
      evt.preventDefault()
      return
    }

    const honeyPot = document.querySelector('input.nogo');
    if(honeyPot && honeyPot.value !== '') {
        evt.preventDefault();
        return;
    }

    const validateResult = this.pristineFormInstance.validate();
    if (!validateResult) {
      evt.preventDefault()
      this.showSummary()
    } else {

      this.hideSummary()
      evt.preventDefault()

      const { success, error, errorDetail, form } = this.getFormContainers()
      let action = form.action;

      let data = new FormData(form)      

      this.getToken(action,data);          
    }
  },

  getToken: function(action, formData) {    
    let id = document.getElementById('clientid');    
    let url = '/api/auth/get-token/' + id.value;    
    fetch(url, {
      method: 'GET'
    }).then((response) => {      
      return response.json();
    }).then((data) => {
      this.postForm(action,formData,data.result);
    }).catch((err) => {
      console.log(err);
    })
  },

  postForm: function(action,data,token) {
    console.log(action);
    console.log(data);
    console.log(token);
    const { success, error, errorDetail, form } = this.getFormContainers()
    fetch(action, {
      method: 'POST',
      body: data,
      headers: {
        'Authorization': 'amx ' + token
      }
    }).then((response) => {
      return response.json();
    }).then((data) => {
      if (data.success) {
        form.style.display = 'none';
        if (success) {
            success.style.display = 'grid'
            this.scrollToContainer(success);
        }
        googleAnalyticsSubmitForm(form);
      } else {
        if (error) {
          error.style.display = 'grid';
          error.style.color = '#ce2027'
        } 
        if (errorDetail) {
          errorDetail.style.color = '#ce2027'
          let message = data.message
          if (message.length > 0) {
            let element = document.createElement('p');
            element.setAttribute('class','form__error-message required')
            element.style.display = 'grid'
            element.textContent = message
            errorDetail.appendChild(element)
            // To scroll and show the error call 'this.scrollToContainer'
            // method (1rst arg accept an string or a DOM obj). For example:
            //    - this.scrollToContainer(error);
            //    - this.scrollToContainer('.some-error-container');
            //    - this.scrollToContainer('#error-message');
          }
          this.scrollToContainer(success);
        }
      }
    }).catch((err) => {
      if (error) {
        error.style.display = 'grid';
        error.style.color = '#ce2027'
        this.scrollToContainer(error);
      }
    })
  },

  getFormContainers: function () {
    return {
        success: document.querySelector('#thank-you'),
        error: document.querySelector('#error-message'),
        errorDetail: document.querySelector('#error-details'),
        form: document.querySelector('form.form')
    }
  },

  resetValidationFromUI: function () {
    const summaryContainer = this.form.querySelector(
      FORM_SELECTORS.HINT_CONTAINER
    );
    this.scrollToContainer(summaryContainer);
    this.resetValidationErrors();
  },

  resetValidationErrors: function () {
    if (this.pristineFormInstance) {
        this.pristineFormInstance.reset();
        this.hideSummary();
    }
    const { success, error, errorDetail, form } = this.getFormContainers();
    if (success) {
        success.style.display = 'none';
    }
    if (error) {
        error.style.display = 'none';
    }
    if (errorDetail) {
        errorDetail.style.display = 'none';
    }
    if (form) {
        form.style.display = 'initial';
    }
  }

}

export default FormValidation
